// Predefined templates for the AI Assistant
// Each template configures the AI assistant for a specific use case

const PREDEFINED_TEMPLATES = [
  {
    id: 'prospect-researcher',
    name: 'Prospect Researcher',
    model: 'gpt-5', // Web search and image capable
    task: 'Research the person mentioned in the input and provide a comprehensive profile including their background, career highlights, recent activities, and any notable achievements or controversies.',
    context: 'Satya Nadella, CEO of Microsoft',
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'A detailed profile of the person including background, career, recent activities, and notable achievements',
        key: 'ai_assistant_profile',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of how the research was conducted',
        key: 'ai_explanation_research',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'data-analyst',
    name: 'Data Analyst',
    model: 'gpt-5', // Advanced intelligence for complex reasoning
    task: 'Analyze the provided data to identify patterns, trends, and insights. Interpret the data and suggest conclusions or recommendations based on the analysis.',
    context: `Sales Data Q1-Q4 2024:
Q1: Revenue: $125,000, Units: 2,500, Avg Deal: $50
Q2: Revenue: $150,000, Units: 2,800, Avg Deal: $53.57
Q3: Revenue: $180,000, Units: 3,200, Avg Deal: $56.25
Q4: Revenue: $220,000, Units: 3,800, Avg Deal: $57.89

Customer Segments:
Enterprise: 40% revenue, 15% units
SMB: 35% revenue, 50% units
Startup: 25% revenue, 35% units`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Key findings and insights from the data analysis',
        key: 'ai_assistant_findings',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Patterns or trends identified in the data',
        key: 'ai_assistant_patterns',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Recommendations based on the data analysis',
        key: 'ai_assistant_recommendations',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of the data analysis methodology',
        key: 'ai_explanation_analysis',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'language-translator',
    name: 'Language Translator',
    model: 'gpt-5-mini', // Fast and capable model
    task: 'Translate the provided text from the source language to the target language, maintaining the meaning, tone, and context of the original content.',
    context: `Source Language: English
Target Language: Spanish
Text to translate: "Good morning! I hope this email finds you well. I wanted to follow up on our meeting last week regarding the new product launch. We are excited to move forward with the collaboration and would like to schedule a call to discuss the next steps. Please let me know your availability for next Tuesday or Wednesday afternoon."`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Translated text',
        key: 'ai_assistant_translation',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Notes about cultural nuances or idioms that required special attention',
        key: 'ai_assistant_translation_notes',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of translation choices and methodology',
        key: 'ai_explanation_translation',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'market-researcher',
    name: 'Market Researcher',
    model: 'gpt-5', // Web search and image capable
    task: 'Research the market for the specified product, service, or industry. Analyze current trends, key competitors, target audience, and provide strategic recommendations.',
    context: 'AI-powered customer service chatbots for e-commerce businesses',
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Market overview and current trends',
        key: 'ai_assistant_market_overview',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Competitor analysis',
        key: 'ai_assistant_competitors',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Target audience insights',
        key: 'ai_assistant_audience',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Strategic recommendations',
        key: 'ai_assistant_recommendations',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of research methodology',
        key: 'ai_explanation_market_research',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'customer-support',
    name: 'Customer Support Agent',
    model: 'gpt-5-mini', // Default choice for most tasks
    task: "Create helpful, empathetic responses to customer inquiries or complaints. Address the customer's concerns fully while maintaining a professional and friendly tone.",
    context: `Customer Inquiry:
From: john.doe@email.com
Subject: Issue with Recent Order #12345

Hi,

I placed an order last week (Order #12345) and was supposed to receive it yesterday, but it hasn't arrived yet. The tracking shows it's still in transit. I need this item for an important presentation tomorrow morning. This is really frustrating as I paid for expedited shipping. Can you please help me figure out what's going on and provide a solution?

Thanks,
John Doe`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Customer response that addresses their concern',
        key: 'ai_assistant_response',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Follow-up questions or additional information that might be needed',
        key: 'ai_assistant_followup',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of response strategy and tone considerations',
        key: 'ai_explanation_support',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'email-composer',
    name: 'Email Composer',
    model: 'gpt-5-mini', // Default choice for most tasks
    task: 'Compose professional, clear, and effective emails based on the provided context and purpose. Ensure the email has an appropriate tone and structure for the intended audience.',
    context: `Email Purpose: Follow up with a potential client after a demo meeting
Recipient: Sarah Johnson, Marketing Director at TechCorp
Context: We had a 30-minute demo last Wednesday showing our marketing automation platform. She seemed interested in the lead scoring and email campaign features. She mentioned they're currently using HubSpot but are not satisfied with the reporting capabilities. Their team of 15 marketers needs better analytics. She asked for pricing information and a trial.
Tone: Professional but friendly
Next Steps: Provide pricing, offer 14-day trial, schedule follow-up call`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Email subject line',
        key: 'ai_assistant_subject',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Email body text',
        key: 'ai_assistant_body',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of email composition strategy and tone choices',
        key: 'ai_explanation_email',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'legal-document-reviewer',
    name: 'Legal Document Reviewer',
    model: 'gpt-5', // Advanced intelligence for complex reasoning
    task: 'Review the provided legal document to identify key terms, potential issues, and suggested improvements. Note that this is not legal advice but an initial analysis.',
    context: `SOFTWARE LICENSE AGREEMENT

This Software License Agreement ("Agreement") is entered into between TechSoft Inc. ("Licensor") and the end user ("Licensee").

1. GRANT OF LICENSE
Licensor grants Licensee a non-exclusive, non-transferable license to use the software for internal business purposes only.

2. RESTRICTIONS
Licensee may not: (a) reverse engineer the software; (b) distribute copies; (c) modify the source code without written permission.

3. TERM AND TERMINATION
This agreement is effective until terminated. Either party may terminate with 30 days written notice. Upon termination, Licensee must destroy all copies.

4. WARRANTY DISCLAIMER
SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.

5. LIMITATION OF LIABILITY
IN NO EVENT SHALL LICENSOR BE LIABLE FOR ANY DAMAGES EXCEEDING THE LICENSE FEE PAID.`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: "Summary of the document's purpose and structure",
        key: 'ai_assistant_legal_summary',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Key terms and clauses identified',
        key: 'ai_assistant_key_terms',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Potential issues or areas of concern',
        key: 'ai_assistant_issues',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Suggested improvements or clarifications',
        key: 'ai_assistant_improvements',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of document review methodology',
        key: 'ai_explanation_legal_review',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'email-parser',
    name: 'Email Parser',
    model: 'gpt-5', // Advanced intelligence for complex reasoning
    task: 'Extract structured data from email content. Identify key information such as dates, amounts, request types, contact details, and action items from email body text.',
    context: `From: vendor@supplycorp.com
To: purchasing@company.com
Subject: Invoice #INV-2024-1247 - Payment Due

Dear Purchasing Team,

Please find attached Invoice #INV-2024-1247 for the recent shipment of office supplies delivered on March 15, 2024.

Invoice Details:
- Invoice Number: INV-2024-1247
- Amount: $3,450.75
- Due Date: April 14, 2024
- PO Number: PO-789456

Items shipped:
- 500 units printer paper ($1,200.00)
- 50 units toner cartridges ($2,100.00)
- Office chairs (5 units) ($150.75)

Payment terms: Net 30 days
Please remit payment to our usual account or contact me at 555-123-4567 if you have any questions.

Action required: Please process payment by April 14, 2024, and send payment confirmation to accounting@supplycorp.com.

Best regards,
Mike Johnson
Accounts Receivable
SupplyCorp Inc.`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Extracted structured data (key-value pairs)',
        key: 'ai_assistant_extracted_data',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Action items identified',
        key: 'ai_assistant_action_items',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of extraction process and confidence levels',
        key: 'ai_explanation_extraction',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'data-validator',
    name: 'Data Validator',
    model: 'gpt-5-mini', // Default choice for most tasks
    task: 'Validate data against business rules and format requirements. Check for completeness, consistency, and compliance with specified rules before allowing workflow to proceed.',
    context: `Business Rules:
- Email must be valid format
- Phone must be 10 digits (US format)
- Age must be 18-65
- Salary must be > $30,000
- Required fields: First Name, Last Name, Email, Phone

Data to validate:
First Name: John
Last Name: 
Email: john.doe@email
Phone: 555-12-3456
Age: 17
Salary: $25,000
Department: Sales
Start Date: 2024-03-32`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Validation result (PASS/FAIL)',
        key: 'ai_assistant_validation_result',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'List of validation errors and issues',
        key: 'ai_assistant_validation_errors',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Suggested corrections',
        key: 'ai_assistant_corrections',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of validation rules applied',
        key: 'ai_explanation_validation',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'document-classifier',
    name: 'Document Classifier',
    model: 'gpt-5', // Advanced intelligence for complex reasoning
    task: 'Classify documents by type, department, priority, or other categorization schemes. Route documents to appropriate workflows based on content and metadata.',
    context: `Document Content:
"URGENT: System Maintenance Notice

Dear IT Team,

We need to schedule emergency maintenance on the production database server this weekend due to a critical security vulnerability discovered in the current version. The maintenance window should be Saturday 2 AM - 6 AM EST.

Please coordinate with the DevOps team and notify all stakeholders about the potential service interruption. We'll need to:
1. Apply security patches
2. Update system configurations  
3. Run full system tests
4. Prepare rollback procedures

This is classified as HIGH PRIORITY due to security implications.

Contact: IT-Security@company.com
Deadline: Implementation by Sunday morning"

Metadata:
- Sender: IT Security Team
- Date: Current
- Contains: "URGENT", "HIGH PRIORITY", "security vulnerability"`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Document classification (type/category)',
        key: 'ai_assistant_document_type',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Confidence score (0-100)',
        key: 'ai_assistant_confidence',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Routing recommendation',
        key: 'ai_assistant_routing',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of classification methodology',
        key: 'ai_explanation_classification',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'approval-drafter',
    name: 'Approval Request Drafter',
    model: 'gpt-5-mini', // Default choice for most tasks
    task: 'Create concise, structured approval requests based on workflow data. Format information for quick review by approvers, highlighting key decision factors.',
    context: `Approval Request Data:
Type: Software Purchase
Requestor: Sarah Chen, Marketing Manager
Department: Marketing
Item: Adobe Creative Suite Enterprise License (10 users)
Cost: $12,000 annually
Justification: Current design tools are outdated. New license will improve productivity for marketing campaigns and reduce freelancer costs by approximately $8,000/year.
Budget: Marketing software budget has $15,000 remaining
Urgency: Medium - needed for Q2 campaign launch
Alternatives Considered: Canva Pro ($3,600/year, limited features), Figma Business ($4,500/year, missing video editing)
ROI: Break-even in 18 months through reduced external design costs`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Approval request subject/title',
        key: 'ai_assistant_approval_subject',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Formatted approval request content',
        key: 'ai_assistant_approval_content',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Key decision factors highlighted',
        key: 'ai_assistant_decision_factors',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of content structure and decision factor selection',
        key: 'ai_explanation_approval',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'notification-generator',
    name: 'Notification Generator',
    model: 'gpt-5-mini', // Default choice for most tasks
    task: 'Generate appropriate notifications for different workflow events and stakeholders. Create clear, actionable messages for email, SMS, or in-app notifications.',
    context: `Event: Purchase Order Approval Required
Workflow: Procurement Process  
Approver: David Martinez, Finance Director
Amount: $45,000
Vendor: TechSupply Solutions
Requested by: Jennifer Lee, Operations Manager
PO Number: PO-2024-0892
Items: 25 laptops for new hires
Urgency: High (new employees start Monday)
Deadline: Approval needed by Friday 5 PM
Additional Context: This exceeds the standard approval threshold of $25,000, requiring director-level approval`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Notification subject/title',
        key: 'ai_assistant_notification_subject',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Notification content',
        key: 'ai_assistant_notification_content',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Suggested urgency level (Low/Medium/High)',
        key: 'ai_assistant_urgency',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of notification content and urgency assessment',
        key: 'ai_explanation_notification',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'decision-router',
    name: 'Decision Router',
    model: 'gpt-5', // Advanced intelligence for complex reasoning
    task: 'Determine the next workflow step based on data analysis and business rules. Route cases to appropriate paths based on content evaluation and conditional logic.',
    context: `Case Data:
Customer Type: Premium
Issue Category: Technical Support  
Severity: High
Product: Enterprise Software
Customer Tier: Platinum (>$100k annual contract)
Issue: Database connectivity failures affecting production
Response Time SLA: 2 hours for Platinum customers
Current Time: 10:30 AM EST
Available Resources: 
- Senior Engineer: Available
- Junior Engineer: Available  
- Database Specialist: In meeting until 11 AM
Previous Escalations: None in last 30 days
Customer Satisfaction Score: 92%

Routing Options:
A) Immediate assignment to Senior Engineer
B) Wait for Database Specialist (30 min delay)
C) Escalate to Engineering Manager
D) Conference call with both Senior Engineer and Database Specialist`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Routing decision (path/next step ID)',
        key: 'ai_assistant_routing_decision',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Decision rationale',
        key: 'ai_assistant_decision_rationale',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Business rules applied',
        key: 'ai_assistant_rules_applied',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of decision-making process and factors considered',
        key: 'ai_explanation_decision',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'data-enricher',
    name: 'Data Enricher',
    model: 'gpt-5', // Advanced intelligence for complex reasoning
    task: 'Enhance existing data by inferring additional fields, standardizing formats, or calculating derived values to provide more complete information for downstream processing.',
    context: `Raw Customer Data:
Name: john smith
Company: acme corp
Email: j.smith@acme-corp.com  
Phone: 5551234567
Address: 123 main st, anytown, ca
Revenue: 2.5M
Employees: 45
Industry: manufacturing

Missing/Incomplete Fields to Enrich:
- Standardized name format
- Full company name
- Phone number formatting
- Complete address with ZIP
- Company size category
- Industry classification (NAICS)
- Annual revenue tier
- Geographic region
- Time zone
- Potential contact preferences`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Enriched/enhanced data',
        key: 'ai_assistant_enriched_data',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'New/derived fields added',
        key: 'ai_assistant_new_fields',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Data standardization applied',
        key: 'ai_assistant_standardization',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of enrichment process and methods',
        key: 'ai_explanation_enrichment',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'summary-generator',
    name: 'Summary Generator',
    model: 'gpt-5', // Advanced intelligence for complex reasoning
    task: 'Create concise summaries of documents, conversations, or process data to facilitate quick review and decision-making in subsequent workflow steps.',
    context: `Meeting Transcript - Project Status Review
Date: March 20, 2024
Duration: 90 minutes
Attendees: Sarah (PM), Mike (Dev Lead), Lisa (Designer), Tom (QA)

Sarah: Let's review where we stand on the mobile app project. We're 3 weeks into the 8-week timeline.

Mike: Backend development is about 60% complete. We've finished the user authentication and data sync modules. The payment integration is taking longer than expected due to security requirements - probably need an extra week there.

Lisa: UI design is nearly done. We have 8 of 12 screens finalized. The remaining screens are waiting for API specifications from backend. Should have everything ready by next Friday.

Tom: QA setup is in progress. Test environment is configured, but we're waiting for the first build to start testing. I'm concerned about the compressed timeline for testing - we only have 2 weeks allocated.

Sarah: Budget is tracking well - we're at 35% spend with 37% timeline complete. The payment integration delay might impact our go-live date. 

Action Items:
- Mike: Provide API specs to Lisa by Thursday
- Lisa: Complete remaining screens by March 29
- Tom: Begin testing as soon as first build is available  
- Sarah: Discuss timeline extension with stakeholders

Risks: Payment integration delay, compressed QA timeline, potential go-live date slip`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Executive summary (1-2 paragraphs)',
        key: 'ai_assistant_exec_summary',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Key points extracted',
        key: 'ai_assistant_key_points',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Action items identified',
        key: 'ai_assistant_action_items',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of summarization approach',
        key: 'ai_explanation_summary',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
  {
    id: 'template-populator',
    name: 'Template Populator',
    model: 'gpt-5-mini', // Default choice for most tasks
    task: 'Populate document templates with workflow data. Format and structure information appropriately for the target document type while maintaining consistency.',
    context: `Template: Employee Onboarding Checklist
Workflow Data:
- Employee Name: Jessica Rodriguez
- Position: Senior Marketing Analyst  
- Department: Marketing
- Start Date: April 1, 2024
- Manager: Robert Chen
- Employee ID: EMP-2024-0156
- Office Location: San Francisco - Floor 3
- Equipment Needed: Laptop, Monitor, Phone
- System Access: Salesforce, HubSpot, Google Workspace, Slack
- Buddy: Maria Santos (Marketing Coordinator)
- Parking Spot: Level 2, Spot 47
- Benefits Enrollment Deadline: April 15, 2024

Template Placeholders to populate:
{{employee_name}}, {{position}}, {{start_date}}, {{manager_name}}, {{employee_id}}, {{office_location}}, {{equipment_list}}, {{system_access_list}}, {{buddy_name}}, {{parking_info}}, {{benefits_deadline}}`,
    explanation: true,
    outputPlaceholders: [
      {
        desc: 'Populated template content',
        key: 'ai_assistant_populated_content',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Missing data fields identified',
        key: 'ai_assistant_missing_data',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'Formatting adjustments made',
        key: 'ai_assistant_formatting',
        type: 'text',
        isExplanation: false,
      },
      {
        desc: 'explanation of template population decisions',
        key: 'ai_explanation_template',
        type: 'text',
        isExplanation: true,
      },
    ],
  },
];

export default PREDEFINED_TEMPLATES;
